import { Box, Code, Flex } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchListHelper } from 'classes/helpers/pitch-list.helper';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextInput } from 'components/common/form/text';
import { getVisibilityBlurb } from 'components/common/pitch-lists/manage-list';
import { t } from 'i18next';
import { DEFAULT_ACCEPT_BTN, IBaseDialog } from 'interfaces/i-dialogs';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { ERROR_MSGS } from 'lib_ts/enums/errors.enums';
import { PitchListOwner } from 'lib_ts/enums/pitch-list.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import {
  ICopyPitchLists,
  ICopyToFrom,
  IPitchList,
} from 'lib_ts/interfaces/pitches';
import { useCallback, useMemo, useState } from 'react';
import { PitchListsService } from 'services/pitch-lists.service';
import { useAuthStore } from 'stores/use-auth-store';
import useListLibraryStore from 'stores/use-list-library-store';

interface IProps extends IBaseDialog {
  from: ICopyToFrom;
  lists: IPitchList[];
}

export const CopyFolderDialog = (props: IProps) => {
  const current = useAuthStore((s) => s.current);
  const reassignOptions = useAuthStore((s) => s.reassignOptions);

  const copyLists = useCallback(
    async (payload: ICopyPitchLists, successMsg?: string) => {
      try {
        if (!payload.to._parent_id || !payload.to._parent_def) {
          NotifyHelper.error({
            message_md: t('common.check-inputs-msg'),
          });
          return;
        }

        useListLibraryStore.setState({ loading: true });

        const result = await PitchListsService.getInstance()
          .copyMany(payload)
          .finally(() => useListLibraryStore.setState({ loading: false }));

        if (!result.success) {
          throw new Error(result.error);
        }

        NotifyHelper.success({
          message_md: successMsg ?? 'Pitch lists copied!',
        });

        // reload lists after copying
        await useListLibraryStore.getState().refreshLists(true);

        props.onClose();
      } catch (e) {
        console.error(e);

        NotifyHelper.error({
          message_md:
            e instanceof Error
              ? e.message
              : `There was an error copying your pitch lists. ${ERROR_MSGS.CONTACT_SUPPORT}`,
        });
      }
    },
    []
  );

  const [target, setTarget] = useState<ICopyToFrom>({ ...props.from });

  const defOptions = useMemo<IOption[]>(
    () => [
      {
        label: 'common.personal',
        invisible: reassignOptions.users.length === 0,
        value: PitchListOwner.User,
      },
      {
        label: 'common.machine',
        invisible:
          !current.machine_lists || reassignOptions.machines.length === 0,
        value: PitchListOwner.Machine,
      },
      {
        label: 'common.team',
        invisible: !current.team_lists || reassignOptions.teams.length === 0,
        value: PitchListOwner.Team,
      },
    ],

    [current, reassignOptions]
  );

  const idOptions = useMemo<IOption[]>(
    () =>
      PitchListHelper.getCopyToIdOptions({
        options: reassignOptions,
        role: current.role,
        teamID: current.teamID,
        parentDef: target._parent_def,
      }),
    [reassignOptions, current, target]
  );

  const warning = useMemo(() => {
    const machineID =
      target._parent_def !== PitchListOwner.Machine
        ? undefined
        : reassignOptions.machines.find((m) => m._id === target._parent_id)
            ?.machineID;

    return getVisibilityBlurb(target._parent_def, machineID);
  }, [target, reassignOptions]);

  return (
    <ErrorBoundary identifier="CopyFolderDialog">
      <CommonDialog
        identifier={props.identifier}
        width={RADIX.DIALOG.WIDTH.MD}
        title="Copy Pitch Lists"
        content={
          <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
            <Box>
              <p>
                Use the form below to copy the following{' '}
                {props.lists.length === 1 ? 'list' : 'lists'} in your folder:
              </p>

              <ul>
                {props.lists
                  .sort((a, b) => {
                    if (a.folder === b.folder) {
                      return StringHelper.safeSort(a.name, b.name);
                    }

                    return StringHelper.safeSort(a.folder, b.folder);
                  })
                  .map((list, iList) => (
                    <li key={iList}>
                      <Code>{list.folder}/</Code>
                      {list.name}
                    </li>
                  ))}
              </ul>
            </Box>

            <CommonFormGrid columns={1}>
              <CommonSelectInput
                id="copy-folder-parent-type"
                name="_parent_def"
                label="Parent Type"
                options={defOptions}
                value={target._parent_def}
                onChange={(v) =>
                  setTarget({
                    ...target,
                    _parent_def: v as PitchListOwner,
                    _parent_id: '',
                  })
                }
                hint_md={warning}
                optional
              />
              <CommonSearchInput
                id="copy-folder-parent"
                name="_parent_id"
                label="Parent"
                options={idOptions}
                values={target._parent_id ? [target._parent_id] : []}
                onChange={(v) =>
                  setTarget({
                    ...target,
                    _parent_id: v[0] ?? '',
                  })
                }
                optional
              />
              <CommonTextInput
                id="copy-folder-destination"
                label="Destination Folder"
                value={target.folder}
                onChange={(v) => setTarget({ ...target, folder: v ?? '' })}
              />
            </CommonFormGrid>
          </Flex>
        }
        buttons={[
          {
            ...DEFAULT_ACCEPT_BTN,
            onClick: () =>
              copyLists({
                lists: props.lists,
                from: props.from,
                to: target,
              }),
          },
        ]}
        onClose={props.onClose}
      />
    </ErrorBoundary>
  );
};
